import { MdlDialogReference, MdlDialogService, IMdlCustomDialogConfiguration } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, HostListener, Inject, InjectionToken } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import * as moment from 'moment';
import { maxLengthInBytes, maxBytesError } from '../../shared/validators';
import { AnnouncementInfo } from './announcement-info';
import { AnnouncementRequest, AnnouncementsInfoStorage } from './announcements-info-storage';
import { AnnouncementDialogComponent, ANNOUNCEMENT_TOKEN } from '../../layout/announcement-dialog.component';
import Quill from 'quill';
import { includes } from 'lodash';

const link = Quill.import('formats/link');

const oldSanitize = link.sanitize.bind(link);

link.sanitize = function (url: string) {
    const sanitized: string = oldSanitize(url);

    if (!sanitized || includes(sanitized, '://')) {
        return sanitized;
    }

    const withHttp = 'https://' + sanitized;

    return withHttp;
};

export const ANNOUNCEMENT_EDIT_TOKEN = new InjectionToken('AnnouncementEditItem');

@Component({
    selector: 'announcement-edit-dialog',
    template: require('./announcement-edit-dialog.component.html'),
    styles: [require('./announcement-edit-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementEditDialogComponent {
    public readonly editorForm: FormGroup;
    public readonly audienceCtrl: FormControl;
    public readonly announcementTitleCtrl: FormControl;
    public readonly effectiveStartDateCtrl: FormControl;
    public readonly effectiveEndDateCtrl: FormControl;
    public readonly announcementTextCtrl: FormControl;
    public readonly isPinnedCtrl: FormControl;
    public readonly auditTrailReasonCtrl: FormControl;
    public id: number;
    public isBusy = false;
    public announcementText = '';
    public isConfirmDialogOpened: boolean;

    public readonly isNew: boolean;
    public readonly wrongDateRangeError = 'wrongDateRangeError';
    public readonly wrongEffectiveStartDate = 'wrongEffectiveStartDate';
    public readonly wrongEffectiveEndDate = 'wrongEffectiveEndDate';
    public readonly missingEffectiveEndDate = 'missingEffectiveEndDate';

    private readonly textMaxBytes = 1048576;
    private readonly titleMaxBytes = 75;
    private readonly effectiveStartDateName = 'effectiveStartDate';
    private readonly effectiveEndDateName = 'effectiveEndDate';

    constructor(
        private dialog: MdlDialogReference,
        private dialogService: MdlDialogService,
        private announcementsInfoStorage: AnnouncementsInfoStorage,
        @Inject(ANNOUNCEMENT_EDIT_TOKEN) announcement: AnnouncementInfo,
    ) {
        this.isNew = !announcement;
        this.id = announcement && announcement.id || 0;
        this.audienceCtrl = new FormControl(announcement && announcement.roles, [Validators.required]);
        this.announcementTitleCtrl = new FormControl(announcement && announcement.title || '', [Validators.required, maxLengthInBytes(this.titleMaxBytes)]);
        this.effectiveStartDateCtrl = new FormControl(announcement && announcement.effectiveStartDate || moment(), [Validators.required]);
        this.effectiveEndDateCtrl = new FormControl(announcement && announcement.effectiveEndDate || null);
        this.announcementTextCtrl = new FormControl(this.announcementText = announcement && announcement.text || '', [Validators.required, maxLengthInBytes(this.textMaxBytes)]);
        this.isPinnedCtrl = new FormControl(announcement && announcement.pinnedFlag);
        this.auditTrailReasonCtrl = new FormControl();
        this.editorForm = new FormGroup(
            {
                'audience': this.audienceCtrl,
                'title': this.announcementTitleCtrl,
                'text': this.announcementTextCtrl,
                [this.effectiveStartDateName]: this.effectiveStartDateCtrl,
                [this.effectiveEndDateName]: this.effectiveEndDateCtrl,
                'isPinned': this.isPinnedCtrl
            },
            this.verifyDates(),
        );

        if (!this.isNew) {
            this.editorForm.addControl('auditTrailReason', this.auditTrailReasonCtrl);
        }
    }

    public onAnnouncementTextChanged($event: { html: string }) {
        this.announcementTextCtrl.setValue($event.html);
    }

    public getTextFieldClasses(control: FormControl): string[] {
        return control.invalid ? ['is-invalid'] : [];
    }

    public get titleErrorMessage(): string {
        if (this.announcementTitleCtrl.hasError('required')) {
            return 'Title is required';
        }

        if (this.announcementTitleCtrl.hasError(maxBytesError)) {
            return `Title is too long`;
        }

        return '';
    }

    public async save() {
        this.isBusy = true;
        if (this.editorForm.invalid) {
            this.isBusy = false;
            return;
        }

        if (!(await this.showConfirm())) {
            return;
        }

        const announcementRequest = new AnnouncementRequest(
            this.id,
            this.announcementTextCtrl.value,
            this.announcementTitleCtrl.value,
            this.effectiveStartDateCtrl.value,
            this.effectiveEndDateCtrl.value,
            this.audienceCtrl.value,
            !!this.isPinnedCtrl.value
        );

        if (this.isNew) {
            await this.announcementsInfoStorage.insert(announcementRequest);
        } else {
            announcementRequest.auditTrailReason = this.auditTrailReasonCtrl.value;

            await this.announcementsInfoStorage.update(announcementRequest);
        }

        this.dialog.hide();
        this.isBusy = false;
    }

    public preview() {
        const announcementPreview = {
            titleAnnouncement: this.announcementTitleCtrl.value,
            textAnnouncement: this.announcementTextCtrl.value,
            isPinned: !!this.isPinnedCtrl.value,
            isNew: false
        };

        const configDialog: IMdlCustomDialogConfiguration = {
            component: AnnouncementDialogComponent,
            providers: [{
                provide: ANNOUNCEMENT_TOKEN, useValue: announcementPreview
            }],
            isModal: true,
            clickOutsideToClose: true,
            classes: 'scrollable'
        };

        this.dialogService.showCustomDialog(configDialog);
    }

    @HostListener('document:keydown.esc')
    public close() {
        this.dialog.hide();
    }

    private async showConfirm(): Promise<boolean> {
        this.isConfirmDialogOpened = true;

        try {
            await this.dialogService.confirm(`Do you want to post this announcement ?`, 'No', 'Yes').toPromise();
            return true;
        } catch {
            return false;
        } finally {
            this.isConfirmDialogOpened = false;
        }
    }

    private verifyDates(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const fromDate: moment.Moment = control.get(this.effectiveStartDateName).value;
            const toDate: moment.Moment = control.get(this.effectiveEndDateName).value;

            if (!(fromDate && fromDate.isValid())) {
                return null;
            } else if (fromDate.isBefore(moment(), 'day')) {
                return { [this.wrongEffectiveStartDate]: {} };
            }

            if (!(toDate && toDate.isValid())) {
                return null;
            } else if (toDate.isBefore(moment(), 'day')) {
                return { [this.wrongEffectiveEndDate]: {} };
            }

            return fromDate.isSameOrBefore(toDate, 'day') ? null : { [this.wrongDateRangeError]: {} };
        };
    }
}
